import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import DropdownMenu from 'components/common/dropdown_menu_v2';
import SetUiLanguage from 'actions/agent_preferences/set_ui_language';
import useExecuteAction from 'components/hooks/use_execute_action';
import { SUPPORTED_LANGUAGES } from 'scripts/i18n/i18n';

// Labels are endonyms (each language shown in its own name) so an agent can find
// theirs regardless of the current UI language. Built per-render with literal
// t() calls, keyed by language code, to stay statically extractable.
function buildLanguageLabels(t) {
  return {
    en: t('English'),
    es: t('Español'),
  };
}

// Options are derived from SUPPORTED_LANGUAGES so the picker can't drift from
// what the build can actually render.
function buildLanguageOptions(t) {
  const labels = buildLanguageLabels(t);
  return SUPPORTED_LANGUAGES.map(value => ({ label: labels[value] || value, value }));
}

export default function LanguageSelector() {
  const { t, i18n } = useTranslation();
  const executeAction = useExecuteAction();

  // Display the active language (which reflects the persisted preference once
  // loaded, falling back to detection for an agent who hasn't chosen). Detections
  // carry a region (es-ES, es-419) that i18next resolves down to the base
  // supported language, so match the dropdown value to that base.
  const currentLanguage = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0];

  return (
    <Wrapper>
      <Description>{t('Choose the language for the agent interface. This applies only to you.')}</Description>
      <DropdownWrapper>
        <DropdownMenu
          data-aid="languageSelector"
          onSelect={language => executeAction(SetUiLanguage, { language })}
          options={buildLanguageOptions(t)}
          value={currentLanguage}
        />
      </DropdownWrapper>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Description = styled.span`
  max-width: 240px;
`;

const DropdownWrapper = styled.div`
  width: 200px;

  > * {
    width: 100%;
  }
`;
